/**
 * Tenant Configuration Modal
 * 
 * Modal for viewing and editing tenant configuration:
 * - Limits tab: storage, agents, members quotas
 * - Processing tab: vision OCR / document processing settings
 * - Usage tab: current usage statistics
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { z } from 'zod';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import {
  DialogHeader,
  DialogTitle,
  Button,
  Skeleton,
} from '@/components/ui';
import { LabeledInput } from '@/components/ui/form-wrappers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useTenantConfig, useUpdateTenantConfig } from '../hooks';
import { TenantUsagePanel } from './TenantUsagePanel';
import { Settings, BarChart3, ScanLine, Sparkles } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type { AvailableModel } from '@/features/agents/types';

interface TenantConfigModalProps {
  tenantId: string;
  onClose: () => void;
}

const PLAN_FEATURES = [
  { slug: 'rag_advanced',           label: 'RAG avanzado',        desc: 'Retrieval-augmented generation — included in every plan.' },
  { slug: 'mcp',                    label: 'MCP Servers',          desc: 'Model Context Protocol server connections.' },
  { slug: 'secret_vault',           label: 'Secret Vault',         desc: 'User secret vault with secure sharing and rotation.' },
  { slug: 'sso',                    label: 'SSO',                  desc: 'Single Sign-On via SAML/OIDC.' },
  { slug: 'web_search',             label: 'Búsqueda web',        desc: 'Web search and fetch — billed per use above the plan quota.' },
] as const;

const configSchema = z.object({
  max_storage_gb: z.coerce.number().min(1).max(10000),
  max_agents: z.coerce.number().min(1).max(1000),
  max_members: z.coerce.number().min(1).max(10000),
  max_collections: z.coerce.number().min(1).max(1000),
  max_documents: z.coerce.number().min(100).max(100000),
  features: z.array(z.string()),
  max_tokens_month: z.coerce.number().min(0),
  vision_ocr_enabled: z.boolean(),
  vision_describe_embedded_images: z.boolean(),
  vision_min_page_text_chars: z.coerce.number().min(0).max(10000),
  vision_model_id: z.string().nullable(),
  vision_model_provider: z.string().nullable(),
});

type ConfigFormData = z.infer<typeof configSchema>;

function useVisionModels() {
  return useQuery({
    queryKey: ['models', 'available', 'vision'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Record<string, AvailableModel[]> }>(
        'models/available'
      );
      const all: AvailableModel[] = Object.values(response.data.data).flat();
      return all.filter((m) => m.supports_vision);
    },
  });
}

export function TenantConfigModal({ tenantId, onClose }: TenantConfigModalProps) {
  const { t } = useI18n();
  const { data: config, isLoading } = useTenantConfig(tenantId);
  const { mutate: updateConfig, isPending } = useUpdateTenantConfig();
  const { data: visionModels = [] } = useVisionModels();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ConfigFormData>({
    resolver: formResolver(configSchema),
    defaultValues: {
      features: [],
      max_tokens_month: 0,
      vision_ocr_enabled: false,
      vision_describe_embedded_images: true,
      vision_min_page_text_chars: 100,
      vision_model_id: null,
      vision_model_provider: null,
    },
  });

  // Reset form when config loads
  useEffect(() => {
    if (config) {
      reset({
        max_storage_gb: config.max_storage_gb,
        max_agents: config.max_agents,
        max_members: config.max_members,
        max_collections: config.max_collections,
        max_documents: config.max_documents,
        features: config.features ?? [],
        max_tokens_month: config.max_tokens_month ?? 0,
        vision_ocr_enabled: config.vision_ocr_enabled ?? false,
        vision_describe_embedded_images: config.vision_describe_embedded_images ?? true,
        vision_min_page_text_chars: config.vision_min_page_text_chars ?? 100,
        vision_model_id: config.vision_model_id ?? null,
        vision_model_provider: config.vision_model_provider ?? null,
      });
    }
  }, [config, reset]);

  const onSubmit = (data: ConfigFormData) => {
    updateConfig(
      { tenantId, data },
      { onSuccess: onClose }
    );
  };

  const visionOcrEnabled = watch('vision_ocr_enabled');
  const selectedModelId = watch('vision_model_id');
  const activeFeatures = watch('features');

  const toggleFeature = (slug: string, enabled: boolean) => {
    const current = watch('features');
    const next = enabled ? [...current, slug] : current.filter((f) => f !== slug);
    setValue('features', next, { shouldDirty: true });
  };

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message:
      t('common.discardChanges.message') ??
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              <>{config?.name} - {t('admin.system.tenantConfig.configuration')}</>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="limits" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="limits" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {t('admin.system.tenantConfig.limits')}
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {t('admin.system.tenantConfig.plan')}
            </TabsTrigger>
            <TabsTrigger value="processing" className="flex items-center gap-2">
              <ScanLine className="h-4 w-4" />
              {t('admin.system.tenantConfig.documentProcessing')}
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {t('admin.system.tenantConfig.usage')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="limits" className="space-y-4 pt-4">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <LabeledInput
                    label={t('admin.system.tenantConfig.maxStorage')}
                    type="number"
                    {...register('max_storage_gb')}
                    error={errors.max_storage_gb?.message}
                  />
                  <LabeledInput
                    label={t('admin.system.tenantConfig.maxAgents')}
                    type="number"
                    {...register('max_agents')}
                    error={errors.max_agents?.message}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <LabeledInput
                    label={t('admin.system.tenantConfig.maxMembers')}
                    type="number"
                    {...register('max_members')}
                    error={errors.max_members?.message}
                  />
                  <LabeledInput
                    label={t('admin.system.tenantConfig.maxCollections')}
                    type="number"
                    {...register('max_collections')}
                    error={errors.max_collections?.message}
                  />
                </div>

                <LabeledInput
                  label={t('admin.system.tenantConfig.maxDocsPerCollection')}
                  type="number"
                  {...register('max_documents')}
                  error={errors.max_documents?.message}
                />

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleClose}>
                    {t('admin.system.tenantConfig.cancel')}
                  </Button>
                  <Button type="submit" disabled={isPending || !isDirty}>
                    {isPending ? t('admin.system.tenantConfig.saving') : t('admin.system.tenantConfig.saveChanges')}
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>

          <TabsContent value="features" className="pt-4">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-3">
                  {PLAN_FEATURES.map(({ slug, label, desc }) => (
                    <div key={slug} className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label htmlFor={`feature-${slug}`} className="text-base font-medium">{label}</Label>
                        <p className="text-sm text-muted-foreground">{desc}</p>
                      </div>
                      <input
                        id={`feature-${slug}`}
                        type="checkbox"
                        className="h-5 w-5 rounded border-gray-300 text-primary cursor-pointer"
                        checked={activeFeatures.includes(slug)}
                        onChange={(e) => toggleFeature(slug, e.target.checked)}
                      />
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-1.5">
                  <LabeledInput
                    label={t('admin.system.tenantConfig.maxTokensMonth')}
                    type="number"
                    {...register('max_tokens_month')}
                    error={errors.max_tokens_month?.message}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.system.tenantConfig.maxTokensMonthDesc')}
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleClose}>
                    {t('admin.system.tenantConfig.cancel')}
                  </Button>
                  <Button type="submit" disabled={isPending || !isDirty}>
                    {isPending ? t('admin.system.tenantConfig.saving') : t('admin.system.tenantConfig.saveChanges')}
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>

          <TabsContent value="usage" className="pt-4">
            <TenantUsagePanel tenantId={tenantId} maxTokensMonth={config?.max_tokens_month ?? null} />
          </TabsContent>

          <TabsContent value="processing" className="pt-4">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Vision OCR enable toggle */}
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="vision_ocr_enabled" className="text-base font-medium">
                      {t('admin.system.tenantConfig.visionOcrEnabled')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('admin.system.tenantConfig.visionOcrEnabledDesc')}
                    </p>
                  </div>
                  <input
                    id="vision_ocr_enabled"
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-300 text-primary cursor-pointer"
                    checked={visionOcrEnabled}
                    onChange={(e) => setValue('vision_ocr_enabled', e.target.checked, { shouldDirty: true })}
                  />
                </div>

                {/* Describe embedded images toggle */}
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="vision_describe_embedded" className="text-base font-medium">
                      {t('admin.system.tenantConfig.visionDescribeEmbedded')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('admin.system.tenantConfig.visionDescribeEmbeddedDesc')}
                    </p>
                  </div>
                  <input
                    id="vision_describe_embedded"
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-300 text-primary cursor-pointer disabled:opacity-50"
                    checked={watch('vision_describe_embedded_images')}
                    disabled={!visionOcrEnabled}
                    onChange={(e) => setValue('vision_describe_embedded_images', e.target.checked, { shouldDirty: true })}
                  />
                </div>

                {/* Min page text threshold */}
                <div className="space-y-1.5">
                  <LabeledInput
                    label={t('admin.system.tenantConfig.visionMinPageText')}
                    type="number"
                    disabled={!visionOcrEnabled}
                    {...register('vision_min_page_text_chars')}
                    error={errors.vision_min_page_text_chars?.message}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.system.tenantConfig.visionMinPageTextDesc')}
                  </p>
                </div>

                {/* Vision model selector */}
                <div className="space-y-1.5">
                  <Label>
                    {t('admin.system.tenantConfig.visionModel')}
                  </Label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!visionOcrEnabled}
                    value={selectedModelId ?? ''}
                    onChange={(e) => {
                      const selected = visionModels.find((m) => m.model_id === e.target.value);
                      setValue('vision_model_id', selected?.model_id ?? null, { shouldDirty: true });
                      setValue('vision_model_provider', selected?.provider_id ?? null, { shouldDirty: true });
                    }}
                  >
                    <option value="">
                      {t('admin.system.tenantConfig.visionModelPlatformDefault')}
                    </option>
                    {visionModels.map((m) => (
                      <option key={m.model_id} value={m.model_id}>
                        {m.display_name} ({m.provider_id})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    {t('admin.system.tenantConfig.visionModelDesc')}
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleClose}>
                    {t('admin.system.tenantConfig.cancel')}
                  </Button>
                  <Button type="submit" disabled={isPending || !isDirty}>
                    {isPending ? t('admin.system.tenantConfig.saving') : t('admin.system.tenantConfig.saveChanges')}
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}